import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormsModule } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, startWith } from 'rxjs/operators';
import {
    SharedModule,
    FormInputComponent,
    InputComponentConfig,
    DataService,
    ItemOf,
} from '@vendure/admin-ui/core';
import { GetCollectionsListDocument, GetCollectionsListQuery, LogicalOperator } from '../generated-types';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
    selector: 'vdr-collection-selector-form-input',
    templateUrl: './collection-selector.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FormsModule, NgSelectModule, SharedModule],
})
export class CollectionSelectorComponent implements FormInputComponent, OnInit {
    searchInput$ = new Subject<string>();
    collections$: Observable<GetCollectionsListQuery['collections']['items']>;
    selectedCollection: ItemOf<GetCollectionsListQuery, 'collections'> | null = null;
    isListInput?: boolean | undefined;
    config: InputComponentConfig;
    readonly: boolean;
    formControl: FormControl;

    constructor(
        private dataService: DataService,
        private cdr: ChangeDetectorRef,
    ) { }

    ngOnInit() {
        const collectionId = this.formControl.value;
        this.collections$ = this.searchInput$.pipe(
            startWith(''),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((term: any) => {
                console.log('Searching for collections with term:', term.term);
                return this.dataService
                    .query(GetCollectionsListDocument, {
                        options: {
                            filter: {
                                id: {
                                    in: collectionId ? [collectionId.toString()] : undefined,
                                },
                                name: { contains: term.term },
                            },
                            filterOperator: LogicalOperator.OR,
                            take: 100,
                        },
                    })
                    .mapStream(data => data.collections.items.filter(c => !c.isPrivate));
            }),
        );
        if (collectionId) {
            this.collections$.subscribe(collections => {
                this.selectedCollection =
                    collections.find(c => c.id.toString() === collectionId.toString()) ?? null;
                this.cdr.markForCheck();
            });
        }
    }

    selectCollection(collection: ItemOf<GetCollectionsListQuery, 'collections'>) {
        this.selectedCollection = collection;
        this.formControl.setValue(collection?.id ?? null);
    }

    trackById(index: number, item: ItemOf<GetCollectionsListQuery, 'collections'>): string {
        return item ? item.id : '';
    }

    compareWith<T extends ItemOf<GetCollectionsListQuery, 'collections'> | string>(o1: T, o2: T) {
        const id1 = typeof o1 === 'string' ? o1 : o1.id;
        const id2 = typeof o2 === 'string' ? o2 : o2.id;
        return id1 === id2;
    }
}
